import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money, formatDateAT } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";
import { usePeriod } from "@/context/PeriodContext";
import { Badge } from "@/components/ui/badge";
import EmptyState from "@/components/EmptyState";
import { TrendingUp, Receipt, FileText, Landmark, History } from "lucide-react";

const KIND_META = {
  revenue: { icon: TrendingUp, tone: "text-emerald-700 bg-emerald-50 border-emerald-200", tr: "Ciro",     de: "Umsatz" },
  expense: { icon: Receipt,    tone: "text-red-700 bg-red-50 border-red-200",             tr: "Gider",    de: "Ausgabe" },
  invoice: { icon: FileText,   tone: "text-slate-700 bg-slate-50 border-slate-200",       tr: "Fatura",   de: "Rechnung" },
  bank:    { icon: Landmark,   tone: "text-blue-700 bg-blue-50 border-blue-200",          tr: "Banka",    de: "Bank" },
};

export default function RecentMovements() {
  const { lang } = useI18n();
  const { periodKey } = usePeriod();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!periodKey) return;
    (async () => {
      try {
        const { data } = await api.get(`/dashboard/recent-movements?period=${periodKey}&limit=10`);
        setItems(data?.items || []);
      } catch {
        setItems([]);
      }
    })();
  }, [periodKey]);

  const t = (tr, de) => (lang === "de" ? de : tr);
  if (items === null) return null;
  if (items.length === 0) {
    return (
      <div className="rounded-xl border bg-background p-4" data-testid="recent-empty">
        <div className="text-sm font-semibold mb-2 flex items-center gap-2">
          <History className="w-4 h-4" /> {t("Son Hareketler", "Letzte Bewegungen")}
        </div>
        <EmptyState
          icon={History}
          title={t("Bu ay için hareket yok", "Keine Bewegungen für diesen Monat")}
          description={t("Gelir / gider ekle — hareketler burada listelenecek.", "Erfasse Umsätze oder Ausgaben — sie erscheinen hier.")}
        />
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-background" data-testid="recent-movements">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="text-sm font-semibold flex items-center gap-2">
          <History className="w-4 h-4" /> {t("Son Hareketler", "Letzte Bewegungen")}
        </div>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {t("Son 10", "Letzte 10")}
        </span>
      </div>
      <ul className="divide-y">
        {items.map((it) => {
          const meta = KIND_META[it.kind] || KIND_META.expense;
          const Icon = meta.icon;
          const isPos = (it.amount || 0) >= 0;
          return (
            <li key={`${it.kind}-${it.id}`} className="flex items-center gap-3 px-4 py-2.5" data-testid={`mv-${it.kind}-${it.id}`}>
              <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${meta.tone}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{it.title || "—"}</div>
                <div className="text-[11px] text-muted-foreground font-mono flex items-center gap-2">
                  <span>{formatDateAT(it.date)}</span>
                  <Badge variant="outline" className="h-4 text-[9px] px-1 border-muted-foreground/25">
                    {lang === "de" ? meta.de : meta.tr}
                  </Badge>
                  {it.period_key && <span className="opacity-60">· {it.period_key}</span>}
                </div>
              </div>
              <div className={`font-mono text-sm font-semibold ${isPos ? "text-emerald-700" : "text-destructive"}`}>
                {isPos ? "+" : ""}{money(it.amount || 0)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
